// src/store/apis/cgtCalculationsApi.ts
import { createApi } from '@reduxjs/toolkit/query/react';
import { customBaseQuery } from '@/lib/baseQuery';
import { ApiResponse } from '@/shared/types';
import type {
  CgtAutofillResponse,
  CgtByDealPropertyResponse,
  CgtCalculationRecord,
  CgtUpsertPayload,
} from '@/pages/tax/routes/CGTCalculation/types/cgt-api.type';

export interface ListCgtCalculationsParams {
  page?: number;
  limit?: number;
  search?: string;
  taxDealId?: string;
  status?: string;
}

export interface ListCgtCalculationsResponse {
  meta: { total: number; page: number; limit: number; pages: number };
  items: CgtCalculationRecord[];
}

const pairTag = (taxDealId: string, propertyId: string) =>
  `${taxDealId}:${propertyId}`;

export const cgtCalculationsApi = createApi({
  reducerPath: 'cgtCalculationsApi',
  baseQuery: customBaseQuery,
  tagTypes: ['CgtCalculations', 'CgtCalculation'],
  endpoints: (builder) => ({
    getCgtCalculations: builder.query<
      ApiResponse<ListCgtCalculationsResponse>,
      ListCgtCalculationsParams | void
    >({
      query: (params) => ({
        url: '/v1/tax/cgt-calculations',
        method: 'GET',
        params: params ?? undefined,
      }),
      providesTags: [{ type: 'CgtCalculations', id: 'LIST' }],
    }),
    getCgtCalculationById: builder.query<
      ApiResponse<CgtCalculationRecord>,
      string
    >({
      query: (id) => ({
        url: `/v1/tax/cgt-calculations/${id}`,
        method: 'GET',
      }),
      providesTags: (_res, _err, id) => [{ type: 'CgtCalculation', id }],
    }),
    getCgtByDealAndProperty: builder.query<
      ApiResponse<CgtByDealPropertyResponse>,
      { taxDealId: string; propertyId: string }
    >({
      query: ({ taxDealId, propertyId }) => ({
        url: `/v1/tax/cgt-calculations/deal/${taxDealId}/property/${propertyId}`,
        method: 'GET',
      }),
      providesTags: (_res, _err, arg) => [
        { type: 'CgtCalculation', id: pairTag(arg.taxDealId, arg.propertyId) },
      ],
    }),
    getCgtAutofill: builder.query<ApiResponse<CgtAutofillResponse>, string>({
      query: (propertyId) => ({
        url: `/v1/tax/cgt-calculations/autofill/property/${propertyId}`,
        method: 'GET',
      }),
    }),
    createCgtCalculation: builder.mutation<
      ApiResponse<CgtCalculationRecord>,
      CgtUpsertPayload & { taxDealId: string; propertyId: string }
    >({
      query: (body) => ({
        url: '/v1/tax/cgt-calculations',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_res, _err, arg) => [
        { type: 'CgtCalculations', id: 'LIST' },
        { type: 'CgtCalculation', id: pairTag(arg.taxDealId, arg.propertyId) },
      ],
    }),
    updateCgtCalculation: builder.mutation<
      ApiResponse<CgtCalculationRecord>,
      { id: string; body: CgtUpsertPayload }
    >({
      query: ({ id, body }) => ({
        url: `/v1/tax/cgt-calculations/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (res, _err, arg) => [
        { type: 'CgtCalculations', id: 'LIST' },
        { type: 'CgtCalculation', id: arg.id },
        ...(res?.data
          ? [
              {
                type: 'CgtCalculation' as const,
                id: pairTag(res.data.taxDealId, res.data.propertyId),
              },
            ]
          : []),
      ],
    }),
    deleteCgtCalculation: builder.mutation<
      ApiResponse<{ id: string; deleted: boolean }>,
      string
    >({
      query: (id) => ({
        url: `/v1/tax/cgt-calculations/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_res, _err, id) => [
        { type: 'CgtCalculations', id: 'LIST' },
        { type: 'CgtCalculation', id },
      ],
    }),
  }),
});

export const {
  useGetCgtCalculationsQuery,
  useGetCgtCalculationByIdQuery,
  useGetCgtByDealAndPropertyQuery,
  useGetCgtAutofillQuery,
  useLazyGetCgtAutofillQuery,
  useCreateCgtCalculationMutation,
  useUpdateCgtCalculationMutation,
  useDeleteCgtCalculationMutation,
} = cgtCalculationsApi;
